/** @module csi/table/matrix/_Processor */
define([
	"dojo/_base/declare",
	"csi/def",
	"csi/heading/_base",
	"csi/html",
	"csi/model/image",
], function(declare, def, csiHeading, html, {getImageSizeMaxHeight}) {

	return declare(null, {
		INVALID_INDEX: -999,

		/** @property {boolean} bAllowOnRootNode - render matrix on root node in view */
		bAllowOnRootNode: false,

		/** @property {boolean} bRelativeToRoot - set to true when Headings in matrix are relative to root class of view rather than target class of Relationship (i.e. RowRootMap, ColumnRootMap). */
		bRelativeToRoot: false,

		// Matrix: matrix configuration
		//config: null,

		// Boolean: indicates if column has no query support for sorting and filtering
		//restrictQuery: false,

		constructor: function(/*Matrix*/matrix) {
			this.config = matrix;
		},

		addDeferred: function(/*Context*/ctx, /*Heading*/h, /*Deferred[]*/dfds) {
			// summary: add any deferreds necessary to resolve before processing matrix
			// FIX: allow Relationship tag under matrix to specify dynamic number of columns
		},

		addDependencyPaths: function(/*Heading*/h, /*String[]*/paths) {
			// summary: add any dependency paths necessary to process matrix
		},

		/**
		 * add any parameters to the request to process matrix
		 * @param {Request} request 
		 */
		addRootRequestParameters(request) { },

		/**
		 * Return nodes store that holds possible nodes for column matrix
		 * @param {Context} ctx
		 * @param {Heading} h
		 * @return {StoreNodes | undefined}
		 */
		getColumnQueryLevelStore: function(ctx, h) { },

		getColumnSpecs: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return column specifications for matrix
			return [];
		},

		/**
		 * Return key to uniquely identify merged cells from matrix for use when comparing view;
		 * by default nothing is returned and other techniques (such as column mapping) can be used
		 * @param {Context} ctx
		 * @param {Spec} spec
		 * @param {number} [containerIndex]
		 * @return {string}
		 */
		getCompareKeyForMergedCells(ctx, spec, containerIndex) {
			return "";
		},

		/**
		 * Extension point allowing view to be affected in order to accommodate the matrix;
		 * this is called for all possible matrices in the view, not just those in the current view
		 * @param {ViewWidget} view 
		 */
		initializeView(view) { },

		/**
		 * Return false if matrix should not apply to the given target class
		 * @param {ReflectionClass} targetClass 
		 * @return {boolean}
		 */
		isValidForClass(targetClass) {
			return true;
		},

		render: function(/*Context*/ctx, /*QueryResult*/qr, /*Spec*/spec, /*Number*/nColumn, /*DOMNode*/td) {
			// summary: render cells of matrix, returning next column index based on number of columns rendered
			var subs = spec.subs;
			var n = subs && subs.length;
			if (n) {
				for (var i = 0; i < n; ++i) {
					this._renderCell(ctx, subs[i], nColumn++, i ? ctx.view.generateCell(subs[i], td.parentNode) : td);
				}
			} else {
				this._renderCell(ctx, spec, nColumn++, td);
			}

			return nColumn;
		},

		shouldRenderLoneSubheader: function(/*Spec*/subSpec) {
			// summary: return true if subheader spec should be rendered on its own
			return false;
		},

		_cacheStore: function(/*Context*/ctx, /*String*/id, /*Store*/store) {
			// summary: track data store for matrix on view
			ctx.view.cacheMatrixStore(id, store);
		},

		_getStore: function(/*Context*/ctx, /*String*/id) {
			// summary: return data store used for matrix by id
			return ctx.view.getMatrixStore(id);
		},

		_renderCell: function(/*Context*/ctx, /*Spec*/spec, /*Number*/nColumn, /*DOMNode*/td, /*Node[]?*/nodes, /*Number[]?*/indices) {
			// summary: render cell of matrix
			const {isComparing} = ctx.view;
			var h = spec.heading;
			var n = Math.max(nodes?.length || 0, indices?.length || 0) || 1;
			var extra;

			if (n > 1) {
				// keep track of extra cells in row and the max number of them in any column
				ctx.cellMerge = ctx.cellMerge || {};
				ctx.cellMerge[nColumn] = extra = new Array(n - 1);
			}

			if (spec.index != null) {
				ctx.keyValue = spec.index;
			}

			if (!ctx.bPrint) {
				// add available height and width to context in case needed for rendering content (i.e. images)
				ctx.availableWidth = ctx.view.scrollData.getAvailableColumnWidth(nColumn);
				ctx.availableHeight = getImageSizeMaxHeight(ctx.prefImageSize);
			}

			for (var i = 0; i < n; ++i) {
				const containerIndex = indices?.[i];

				if (nodes && (nodes[i] !== undefined)) {
					ctx.setNode(nodes[i]);
				}

				if (containerIndex != null) {
					ctx.keyValue = containerIndex;
				}

				if (i) {
					td = extra[i - 1] = ctx.view.generateCell(spec, td.parentNode);
				}

				// set CSS variable for allowable image height
				ctx.view.applyTableImageHeight(td, ctx.availableHeight);

				// matrix cells require a unique key to be able to identify cells when in comparison mode
				// in some cases (when multiple rows), so add that here
				if (isComparing) {
					const key = this.getCompareKeyForMergedCells(ctx, spec, containerIndex);
					html.setDataAttribute(td, html.TABLE_CELL_COMPARE_KEY, key ?? "");
				}

				if (ctx.getNode() && (ctx.keyValue !== this.INVALID_INDEX)) {
					this._renderCellContent(ctx, h, td, spec, containerIndex);
				} else {
					// if no node in context, just add styling
					def.processClass(h, td, html.CSS_NOT_APPLICABLE);
				}

				spec.applyCss(td);
			}

			ctx.keyValue = null;
		},

		/**
		 * Render cell wrapper. Potential extension point to override cell rendering
		 * @param {Context} ctx
		 * @param {Heading} h
		 * @param {DomNode} td
		 * @param {Spec} columnSpec - used in overrides
		 * @param {Number} index - used in overrides
		 */
		_renderCellContent: function(ctx, h, td, columnSpec, index) {
			csiHeading.toHtml(ctx, h, td);

			// override automation value for matrices to get proper label - cannot rely on Heading since it can be reused in column matrices
			const {parentSpec} = columnSpec;
			const automationText = parentSpec ? `${parentSpec.getText()}:${columnSpec.getText()}` : columnSpec.getText();
			html.setDataAttribute(td, html.DATA_AUTOMATION, automationText);
		}
	});
});